import * as XLSX from 'xlsx';

interface CajaRecordType {
  caja_session_record_id: number;
  local_id: number;
  session_id: number;
  user_id: number;
  user_name: string;
  session_start: string;
  session_end: string;
  initial_amount: number;
  final_amount: number;
  canceled?: boolean;
  shift_id?: number | null;
  shift_name?: string | null;
  details: {
    types: {
      [payment_type_id: string]: {
        payment_type_name: string;
        system: number;
        reported: number;
        matched: boolean;
        tags?: { tag: string; amount: number }[];
      };
    };
    caja_movements: {
      [workstation_id: string]: {
        name: string;
        system: number;
        reported: number;
        tags?: { tag: string; amount: number }[];
      };
    };
    tips: {
      [tip_name: string]: {
        name: string;
        system: number;
        reported: number;
        matched?: boolean;
        tags?: { tag: string; amount: number }[];
      };
    };
  };
  difference: number;
  movements_matched: boolean;
  movements_difference: number;
  tips_matched: boolean;
  tip_difference: number;
  notes?: string;
  local_timestamp: string;
  timestamp: string;
}

const formatDateTime = (timestamp: string): string => {
  if (!timestamp) return 'N/A';
  const date = new Date(timestamp);
  if (isNaN(date.getTime())) return 'N/A';
  
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = date.getFullYear();
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  
  return `${day}/${month}/${year} ${hours}:${minutes}`;
};

const getEstado = (record: CajaRecordType): string => {
  if (record.canceled) return 'Cancelado';
  return record.difference === 0 ? 'Cuadrado' : 'No Cuadrado';
};

const transformArqueosData = (arqueos: CajaRecordType[]) => {
  return arqueos.map(arqueo => {
    // Extract payment type totals from details
    const paymentTypes = arqueo.details?.types || {};
    
    // Calculate totals by payment type
    const cashTotal = Object.values(paymentTypes)
      .filter(pt => pt.payment_type_name?.toLowerCase().includes('efectivo'))
      .reduce((sum, pt) => sum + (pt.reported || 0), 0);
      
    const creditTotal = Object.values(paymentTypes)
      .filter(pt => pt.payment_type_name?.toLowerCase().includes('crédito'))
      .reduce((sum, pt) => sum + (pt.reported || 0), 0);
      
    const debitTotal = Object.values(paymentTypes)
      .filter(pt => pt.payment_type_name?.toLowerCase().includes('débito'))
      .reduce((sum, pt) => sum + (pt.reported || 0), 0);
      
    const transferTotal = Object.values(paymentTypes)
      .filter(pt => pt.payment_type_name?.toLowerCase().includes('transferencia'))
      .reduce((sum, pt) => sum + (pt.reported || 0), 0);
      
    const qrTotal = Object.values(paymentTypes)
      .filter(pt => pt.payment_type_name?.toLowerCase().includes('qr'))
      .reduce((sum, pt) => sum + (pt.reported || 0), 0);
    
    // Calculate tips total
    const tipsTotal = Object.values(arqueo.details?.tips || {})
      .reduce((sum, tip) => sum + (tip.reported || 0), 0);
    
    // Calculate expected total (system amounts)
    const expectedTotal = Object.values(paymentTypes)
      .reduce((sum, pt) => sum + (pt.system || 0), 0);
      
    const expectedTips = Object.values(arqueo.details?.tips || {})
      .reduce((sum, tip) => sum + (tip.system || 0), 0);
    
    // Calculate reported total
    const reportedTotal = Object.values(paymentTypes)
      .reduce((sum, pt) => sum + (pt.reported || 0), 0);

    return {
      'ID Arqueo': arqueo.caja_session_record_id,
      'Usuario': arqueo.user_name,
      'Turno': arqueo.shift_name || 'N/A',
      'Inicio Sesión': formatDateTime(arqueo.session_start),
      'Fin Sesión': formatDateTime(arqueo.session_end),
      'Estado': getEstado(arqueo),
      'Efectivo': Math.round(cashTotal),
      'Crédito': Math.round(creditTotal),
      'Débito': Math.round(debitTotal),
      'Transferencia': Math.round(transferTotal),
      'QR': Math.round(qrTotal),
      'Total Reportado': Math.round(reportedTotal),
      'Total Propinas': Math.round(tipsTotal),
      'Esperado Sistema': Math.round(expectedTotal),
      'Esperado Propinas': Math.round(expectedTips),
      'Diferencia Movimientos': Math.round(arqueo.movements_difference || 0),
      'Diferencia Propinas': Math.round(arqueo.tip_difference || 0),
      'Diferencia Total': Math.round(arqueo.difference || 0),
      'Movimientos Cuadrados': arqueo.movements_matched ? 'Sí' : 'No',
      'Propinas Cuadradas': arqueo.tips_matched ? 'Sí' : 'No',
      'Cancelado': arqueo.canceled ? 'Sí' : 'No',
      'Notas': arqueo.notes || ''
    };
  });
};

export const exportArqueosToExcel = (
  arqueos: CajaRecordType[],
  startDate: string,
  endDate: string
) => {
  try {
    // Transform data
    const data = transformArqueosData(arqueos);

    // Create workbook and worksheet
    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.json_to_sheet(data);

    // Set column widths
    ws['!cols'] = [
      { wch: 12 }, // ID Arqueo
      { wch: 20 }, // Usuario
      { wch: 20 }, // Turno
      { wch: 18 }, // Inicio Sesión
      { wch: 18 }, // Fin Sesión
      { wch: 15 }, // Estado
      { wch: 12 }, // Efectivo
      { wch: 12 }, // Crédito
      { wch: 12 }, // Débito
      { wch: 15 }, // Transferencia
      { wch: 10 }, // QR
      { wch: 15 }, // Total Reportado
      { wch: 15 }, // Total Propinas
      { wch: 15 }, // Esperado Sistema
      { wch: 15 }, // Esperado Propinas
      { wch: 20 }, // Diferencia Movimientos
      { wch: 18 }, // Diferencia Propinas
      { wch: 15 }, // Diferencia Total
      { wch: 20 }, // Movimientos Cuadrados
      { wch: 18 }, // Propinas Cuadradas
      { wch: 12 }, // Cancelado
      { wch: 30 }  // Notas
    ];

    // Add worksheet to workbook
    XLSX.utils.book_append_sheet(wb, ws, 'Arqueos');

    // Generate filename with date range
    const startDateFormatted = startDate.split('T')[0];
    const endDateFormatted = endDate.split('T')[0];
    const filename = `Arqueos_${startDateFormatted}_a_${endDateFormatted}.xlsx`;

    // Write file
    XLSX.writeFile(wb, filename);

    return true;
  } catch (error) {
    console.error('Error exporting to Excel:', error);
    return false;
  }
};

export const exportFilteredArqueos = (
  arqueos: CajaRecordType[],
  startDate: string,
  endDate: string
) => {
  if (arqueos.length === 0) {
    return false;
  }
  return exportArqueosToExcel(arqueos, startDate, endDate);
};
